/**
 * SavedGrants — `/grants/saved`. Wave 4 v2 executive redesign.
 *
 * Structural parity with GrantFinder anchor: PageHeader primitive,
 * Card primitive routed through --ds-radius-card, Heading level=3
 * for card titles, tight padding, hairline separators, 4-level
 * typographic scale.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bookmark, Building2, ArrowUpRight } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import GrantShell from "@/components/grants/GrantShell";
import EligibilityBadge from "@/components/grants/EligibilityBadge";
import SaveGrantButton from "@/components/grants/SaveGrantButton";
import { PageHeader } from "@/design-system/components/PageHeader";
import { Card } from "@/design-system/components/Card";
import { Heading } from "@/design-system/components/Heading";
import { toast } from "sonner";

export default function SavedGrants() {
  const [state, setState] = useState({ loading: true, items: [] });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const res = await api.get("/grants/saved");
        if (!cancelled) setState({ loading: false, items: res.data.items || [] });
      } catch (err) {
        if (!cancelled) {
          setState({ loading: false, items: [] });
          toast.error(formatApiError(err));
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <GrantShell testId="saved-grants-root">
      <PageHeader
        eyebrow="Grant Navigator"
        title="Saved Grants"
        description="Grants you've bookmarked appear here with a fresh eligibility read each time you visit."
        data-testid="saved-grants-header"
      />

      {state.loading && (
        <div
          className="mt-6 text-[13px]"
          style={{ color: "var(--ds-color-ink-muted)" }}
        >
          Loading saved grants…
        </div>
      )}

      {!state.loading && state.items.length === 0 && (
        <div
          data-testid="saved-grants-empty"
          className="mt-8 rounded-[var(--ds-radius-card)] p-10 text-center"
          style={{
            background: "var(--ds-color-surface)",
            border: "1px dashed var(--ds-color-border-strong)",
          }}
        >
          <Bookmark
            className="mx-auto w-5 h-5"
            style={{ color: "var(--ds-color-accent)" }}
            aria-hidden="true"
          />
          <p
            className="mt-3 text-[14px] font-semibold"
            style={{ color: "var(--ds-color-ink)" }}
          >
            Nothing saved yet.
          </p>
          <p
            className="mt-2 text-[13px]"
            style={{ color: "var(--ds-color-ink-muted)" }}
          >
            Browse the Grant Finder and tap "Save this grant" on any card to pin it here.
          </p>
          <Link
            to="/grants"
            data-testid="saved-grants-cta-finder"
            className="mt-6 inline-flex items-center gap-2 rounded-[var(--ds-radius-md)] px-4 py-2 text-[13px] font-semibold transition-colors"
            style={{
              background: "var(--ds-color-accent)",
              color: "var(--ds-color-accent-ink)",
              transitionDuration: "var(--ds-duration-fast)",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "var(--ds-color-accent-strong)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "var(--ds-color-accent)")
            }
          >
            Open Grant Finder
          </Link>
        </div>
      )}

      {!state.loading && state.items.length > 0 && (
        <div
          data-testid="saved-grants-list"
          className="mt-2 grid grid-cols-1 lg:grid-cols-2 gap-4"
        >
          {state.items.map((it) => (
            <Card
              key={it.grant.id}
              data-testid={`saved-grant-card-${it.grant.id}`}
              className="flex flex-col rounded-[var(--ds-radius-card)]"
            >
              <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div
                    className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold"
                    style={{ color: "var(--ds-color-ink-subtle)" }}
                  >
                    <Building2 className="w-3 h-3" aria-hidden="true" />
                    <span className="truncate">{it.grant.agency}</span>
                  </div>
                  <Heading
                    level={3}
                    size="lg"
                    weight="semibold"
                    className="mt-1 leading-snug"
                  >
                    <Link
                      to={`/grants/${it.grant.id}`}
                      className="hover:underline"
                      style={{ color: "var(--ds-color-ink)" }}
                    >
                      {it.grant.title}
                    </Link>
                  </Heading>
                </div>
                <EligibilityBadge status={it.eligibility_snapshot.status} />
              </div>
              <p
                className="px-4 text-[13px] leading-snug line-clamp-3"
                style={{ color: "var(--ds-color-ink-muted)" }}
              >
                {it.grant.program_summary}
              </p>
              <div
                className="mt-4 flex items-center justify-between gap-3 border-t px-4 py-2.5"
                style={{ borderColor: "var(--ds-color-border)" }}
              >
                <Link
                  to={`/grants/${it.grant.id}`}
                  className="inline-flex items-center gap-1.5 text-[13px] font-semibold hover:underline"
                  style={{ color: "var(--ds-color-accent)" }}
                >
                  Open detail
                  <ArrowUpRight className="w-3.5 h-3.5" aria-hidden="true" />
                </Link>
                <SaveGrantButton
                  grantId={it.grant.id}
                  initialSaved={true}
                  testIdPrefix={`saved-grant-remove-${it.grant.id}`}
                  onChange={({ saved }) => {
                    if (!saved) {
                      setState((s) => ({
                        ...s,
                        items: s.items.filter((x) => x.grant.id !== it.grant.id),
                      }));
                    }
                  }}
                />
              </div>
            </Card>
          ))}
        </div>
      )}
    </GrantShell>
  );
}
